/**
 * slide-tracking.js
 * Fenge Section Level Analytics - Slideshow Tracking Module
 *
 * 负责追踪:
 *   - slide_change : 轮播图手动或自动切换（Banner Slideshow / Slideshow 等）
 *
 * 支持的轮播类型:
 *   1. Henry-banner-slideshow (.hybrid-slider-container) — 自定义 fade 轮播
 *   2. slideshow.liquid (slideshow-element)              — 原生 Shopify 轮播
 *   3. 通用 slider-element 轮播
 *
 * @version 1.0.0
 */

(function () {
  'use strict';

  // ─── 工具函数 ────────────────────────────────────────────────────────────────

  function publishEvent(eventName, eventData) {
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
      return;
    }
    try {
      window.Shopify.analytics.publish(eventName, eventData);
    } catch (e) {
      // 生产环境静默
    }
  }

  function getSectionId(el) {
    const wrapper = el.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : '';
  }

  /**
   * 获取 Slide 的素材 URL（图片 src 或 data-* 属性）
   * @param {Element} slideEl
   * @returns {string}
   */
  function getSlideImageSrc(slideEl) {
    if (!slideEl) return '';
    const img =
      slideEl.querySelector('.hybrid-image.img-desktop') ||
      slideEl.querySelector('.banner__image') ||
      slideEl.querySelector('img');
    if (!img) return '';
    // 取原始 src（去掉 Shopify CDN 的尺寸参数以作为素材唯一标识）
    const src = img.getAttribute('src') || img.dataset.src || '';
    return src.split('?')[0].split('_').slice(0, -1).join('_') || src;
  }

  // ─── Henry Hybrid Slider 追踪 ────────────────────────────────────────────────

  /**
   * 为 Henry-banner-slideshow 的 hybrid-slider-container 挂载追踪
   * hybrid slider 通过直接操作 classList('active') 切换 slide，
   * 我们用 MutationObserver 监听 class 变化。
   * @param {Element} slider
   */
  function observeHybridSlider(slider) {
    const sectionId = getSectionId(slider);
    const slides = Array.from(slider.querySelectorAll('.hybrid-slide'));
    if (slides.length <= 1) return;

    let lastActiveIndex = slides.findIndex((s) => s.classList.contains('active'));

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((mut) => {
        if (
          mut.type !== 'attributes' ||
          mut.attributeName !== 'class'
        )
          return;

        const slideEl = mut.target;
        if (!slideEl.classList.contains('active')) return;

        const newIndex = slides.indexOf(slideEl);
        if (newIndex === lastActiveIndex) return;

        const fromIndex = lastActiveIndex;
        lastActiveIndex = newIndex;

        publishEvent('slide_change', {
          section_id: sectionId,
          section_type: 'banner-slideshow',
          from: fromIndex,
          to: newIndex,
          slide_img: getSlideImageSrc(slideEl),
          total_slides: slides.length,
          page_path: window.location.pathname,
        });
      });
    });

    slides.forEach((slide) => {
      mo.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /**
   * 初始化所有 Henry hybrid-slider 的追踪
   */
  function initHybridSliderTracking(scope) {
    const sliders = (scope || document).querySelectorAll(
      '.hybrid-slider-container'
    );
    sliders.forEach(observeHybridSlider);
  }

  // ─── Shopify Native slideshow-element 追踪 ───────────────────────────────────

  /**
   * 为 slideshow-element / slider-element 挂载追踪
   * Native slideshow 使用 SliderElement 组件，监听其 slide:change 自定义事件。
   * @param {Element} sliderEl
   */
  function observeNativeSlider(sliderEl) {
    const sectionId = getSectionId(sliderEl);
    let lastIndex = 0;

    // Shopify theme component 触发 'slider:change' 事件
    const handleChange = (event) => {
      const newIndex =
        event.detail?.currentIndex ??
        event.detail?.index ??
        -1;
      if (newIndex < 0 || newIndex === lastIndex) return;

      const slides = Array.from(
        sliderEl.querySelectorAll('.banner, [data-type="image"], [data-type="video"]')
      );
      const fromIndex = lastIndex;
      lastIndex = newIndex;

      publishEvent('slide_change', {
        section_id: sectionId,
        section_type: 'slideshow',
        from: fromIndex,
        to: newIndex,
        slide_img: getSlideImageSrc(slides[newIndex]),
        total_slides: slides.length,
        page_path: window.location.pathname,
      });
    };

    // 监听多个可能的事件名称（不同 Shopify theme 版本差异）
    sliderEl.addEventListener('slider:change', handleChange);
    sliderEl.addEventListener('slideshow:change', handleChange);

    // 同时监听 dot 按钮点击（部分 section 不触发 slider:change）
    const dotsContainer = document.querySelector(
      `slider-dots[aria-controls="${sliderEl.id}"]`
    );
    if (dotsContainer) {
      dotsContainer.addEventListener('click', (e) => {
        const dotBtn = e.target.closest('button[data-index]');
        if (!dotBtn) return;
        const newIndex = parseInt(dotBtn.dataset.index || '0', 10);
        if (newIndex === lastIndex) return;

        const slides = Array.from(
          sliderEl.querySelectorAll('.banner, [data-type="image"], [data-type="video"]')
        );
        const fromIndex = lastIndex;
        lastIndex = newIndex;

        publishEvent('slide_change', {
          section_id: sectionId,
          section_type: 'slideshow',
          from: fromIndex,
          to: newIndex,
          slide_img: getSlideImageSrc(slides[newIndex]),
          total_slides: slides.length,
          page_path: window.location.pathname,
        });
      });
    }
  }

  /**
   * 初始化所有 slideshow-element / slideshow-hero 的追踪
   */
  function initNativeSliderTracking(scope) {
    const sliders = (scope || document).querySelectorAll(
      'slideshow-element, slider-element.slideshow'
    );
    sliders.forEach(observeNativeSlider);
  }

  // ─── Flickity Slider 追踪 ───────────────────────────────────────────────────

  /**
   * 追踪 Flickity 轮播
   * 许多 Shopify Section 使用 Flickity，我们通过轮询或监听其特定 DOM 变化来支持
   */
  function initFlickityTracking(scope) {
    const flickitySliders = (scope || document).querySelectorAll('.flickity-enabled, [data-flickity]');
    
    flickitySliders.forEach(slider => {
      // 避免重复挂载
      if (slider.dataset.flickityTrackingActive) return;
      slider.dataset.flickityTrackingActive = 'true';

      const sectionId = getSectionId(slider);
      
      // Flickity 通常会将实例挂载在 jQuery 数据或原生属性上
      // 我们通过监听其 'change' 事件（如果能获取到实例）或点击 dot/arrow
      const checkFlickity = setInterval(() => {
        const flkty = window.Flickity && window.Flickity.data(slider);
        if (flkty) {
          clearInterval(checkFlickity);
          flkty.on('change', (index) => {
            publishEvent('slide_change', {
              section_id: sectionId,
              section_type: 'flickity-slider',
              from: -1, // Flickity 不易获取上一个索引，传 -1
              to: index,
              slide_img: getSlideImageSrc(flkty.selectedElement),
              total_slides: flkty.slides.length,
              page_path: window.location.pathname,
            });
          });
        }
      }, 1000);

      // 10秒后停止轮询
      setTimeout(() => clearInterval(checkFlickity), 10000);
    });
  }

  // ─── Beae (Swiper) 追踪 ──────────────────────────────────────────────────────

  /**
   * 追踪 Beae 页面构建器生成的 Swiper 轮播
   */
  function initBeaeSwiperTracking(scope) {
    const swipers = (scope || document).querySelectorAll('.beae-swiper-container');
    
    swipers.forEach(container => {
      if (container.dataset.beaeTrackingActive) return;
      container.dataset.beaeTrackingActive = 'true';

      const sectionId = getSectionId(container);

      // Swiper 实例通常在 DOM 元素的 .swiper 属性中
      const checkSwiper = setInterval(() => {
        const swiper = container.swiper;
        if (swiper) {
          clearInterval(checkSwiper);
          swiper.on('slideChange', () => {
            publishEvent('slide_change', {
              section_id: sectionId,
              section_type: 'beae-swiper',
              from: swiper.previousIndex,
              to: swiper.realIndex,
              slide_img: getSlideImageSrc(swiper.slides[swiper.activeIndex]),
              total_slides: swiper.slides.length,
              page_path: window.location.pathname,
            });
          });
        }
      }, 1000);

      setTimeout(() => clearInterval(checkSwiper), 10000);
    });
  }

  // ─── 初始化入口 ───────────────────────────────────────────────────────────────

  function init(scope) {
    if (
      document.documentElement.classList.contains('shopify-design-mode') ||
      window.Shopify?.designMode
    ) {
      return;
    }
    initHybridSliderTracking(scope);
    initNativeSliderTracking(scope);
    initFlickityTracking(scope);
    initBeaeSwiperTracking(scope);
    initScrollFallbackTracking(scope);
  }

  // ─── 滚动/划动兜底追踪 (Scroll-Snap Fallback) ───────────────────────────────

  /**
   * 兜底逻辑：追踪所有带滚动属性的容器（针对手势划动）
   * 使用 IntersectionObserver 监听子元素（Slide）的可见性
   */
  function initScrollFallbackTracking(scope) {
    // 寻找可能的滚动容器：ID 以 Slider- 开头，或带有 scroll-area 类的
    const scrollContainers = (scope || document).querySelectorAll(
      '[id^="Slider-"], .scroll-area, .grid-scroll'
    );

    scrollContainers.forEach((container) => {
      // 如果已经被专门的处理器追踪了，则跳过
      if (
        container.dataset.flickityTrackingActive ||
        container.dataset.beaeTrackingActive ||
        container.tagName === 'SLIDESHOW-ELEMENT' ||
        container.tagName === 'SLIDER-ELEMENT'
      ) {
        return;
      }

      const sectionId = getSectionId(container);
      const slides = Array.from(container.children).filter(
        (child) => child.nodeType === 1 && !child.classList.contains('no-track')
      );

      if (slides.length <= 1) return;

      let currentActiveIndex = -1;

      // 使用 IO 观察哪一个 slide 处于容器中心
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              const slide = entry.target;
              const index = slides.indexOf(slide);

              if (index !== -1 && index !== currentActiveIndex) {
                const fromIndex = currentActiveIndex;
                currentActiveIndex = index;

                // 首次加载（-1 -> 0）不触发切换事件，只记录初始位置
                if (fromIndex !== -1) {
                  publishEvent('slide_change', {
                    section_id: sectionId,
                    section_type: 'scroll-snap',
                    from: fromIndex,
                    to: index,
                    slide_img: getSlideImageSrc(slide),
                    total_slides: slides.length,
                    page_path: window.location.pathname,
                  });
                }
              }
            }
          });
        },
        {
          root: container,
          threshold: 0.6, // 60% 面积进入容器即视为激活
        }
      );

      slides.forEach((slide) => observer.observe(slide));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // 支持 Shopify 主题编辑器动态加载 section
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
  });
})();
